type Constructor<T = unknown> = new (...args: never[]) => T;
type Token<T = unknown> = InjectionToken<T> | Constructor<T>;

type ParamTokenMap = Map<number, InjectionToken<unknown>>;

interface ClassProvider<T> {
  kind: "class";
  useClass: Constructor<T>;
  singleton: boolean;
  instance?: T;
}

interface InstanceProvider<T> {
  kind: "instance";
  value: T;
}

type Provider<T> = ClassProvider<T> | InstanceProvider<T>;

const injectionTokens = new WeakMap<Function, ParamTokenMap>();

function getParamTokens(target: Function): ParamTokenMap {
  let tokens = injectionTokens.get(target);
  if (!tokens) {
    tokens = new Map<number, InjectionToken<unknown>>();
    injectionTokens.set(target, tokens);
  }
  return tokens;
}

function toTokenKey<T>(token: Token<T>): InjectionToken<T> {
  return token as InjectionToken<T>;
}

function readDesignParamTypes(target: Function): InjectionToken<unknown>[] {
  const reflectApi = Reflect as typeof Reflect & {
    getMetadata?: (key: string, target: object) => unknown;
  };
  const types = reflectApi.getMetadata?.("design:paramtypes", target);
  if (!Array.isArray(types)) {
    return [];
  }
  return types as InjectionToken<unknown>[];
}

function resolveConstructorArgs(
  container: DependencyContainer,
  ctor: Constructor,
): unknown[] {
  const explicitTokens = injectionTokens.get(ctor) || new Map();
  const designTypes = readDesignParamTypes(ctor);

  const maxExplicitIndex = Math.max(-1, ...explicitTokens.keys());
  const argCount = Math.max(
    ctor.length,
    designTypes.length,
    maxExplicitIndex + 1,
  );

  return Array.from({ length: argCount }, (_, index) => {
    const token = explicitTokens.get(index) || designTypes[index];
    if (!token) {
      throw new Error(
        `Cannot resolve dependency #${index} of ${ctor.name || "Anonymous"}: token is not specified`,
      );
    }
    return container.resolve(token);
  });
}

export type InjectionToken<T = unknown> = Constructor<T> | string | symbol;

export class DependencyContainer {
  private readonly providers = new Map<
    InjectionToken<unknown>,
    Provider<unknown>
  >();

  registerInstance<T>(token: InjectionToken<T>, instance: T): this {
    this.providers.set(token, { kind: "instance", value: instance });
    return this;
  }

  register<T>(
    token: InjectionToken<T>,
    provider: {
      useClass: Constructor<T>;
      singleton?: boolean;
    },
  ): this {
    this.providers.set(token, {
      kind: "class",
      useClass: provider.useClass,
      singleton: Boolean(provider.singleton),
    });
    return this;
  }

  registerSingleton<T>(
    tokenOrCtor: InjectionToken<T> | Constructor<T>,
    ctor?: Constructor<T>,
  ): this {
    const token = toTokenKey(tokenOrCtor);
    const useClass = ctor || (tokenOrCtor as Constructor<T>);
    return this.register(token, { useClass, singleton: true });
  }

  isRegistered<T>(token: InjectionToken<T>): boolean {
    return this.providers.has(token);
  }

  resolve<T>(tokenOrCtor: Token<T>): T {
    const token = toTokenKey(tokenOrCtor);
    const provider = this.providers.get(token) as Provider<T> | undefined;

    if (provider?.kind === "instance") {
      return provider.value;
    }

    if (provider?.kind === "class") {
      if (provider.singleton && provider.instance) {
        return provider.instance;
      }
      const args = resolveConstructorArgs(this, provider.useClass);
      const instance = new provider.useClass(...(args as never[]));
      if (provider.singleton) {
        provider.instance = instance;
      }
      return instance;
    }

    if (typeof tokenOrCtor === "function") {
      const args = resolveConstructorArgs(this, tokenOrCtor);
      return new tokenOrCtor(...(args as never[]));
    }

    throw new Error(`Token is not registered: ${String(token)}`);
  }
}

export const container = new DependencyContainer();

export function injectable() {
  return <T extends Constructor>(target: T): void => {
    if (!container.isRegistered(target)) {
      container.register(target, { useClass: target, singleton: false });
    }
  };
}

export function singleton() {
  return <T extends Constructor>(target: T): void => {
    container.registerSingleton(target, target);
  };
}

export function inject<T>(token: InjectionToken<T>) {
  return (
    target: object,
    _propertyKey: string | symbol | undefined,
    index: number,
  ): void => {
    const ctor = typeof target === "function" ? target : target.constructor;
    getParamTokens(ctor).set(index, token);
  };
}
